import { Button, Card, Col, InputNumber, Row, Space, Tag, Tooltip, Typography } from "antd";
import { DeleteOutlined, EnvironmentOutlined, PlusOutlined } from "@ant-design/icons";

export const MAX_EMPLOYEE_LOCATIONS = 5;

export const hasValidEmployeeLocations = (locations = []) =>
  locations.length > 0 &&
  locations.length <= MAX_EMPLOYEE_LOCATIONS &&
  locations.every((location) => {
    const latitude = Number(location?.latitude);
    const longitude = Number(location?.longitude);
    return (
      location?.latitude !== null &&
      location?.latitude !== undefined &&
      location?.longitude !== null &&
      location?.longitude !== undefined &&
      Number.isFinite(latitude) &&
      latitude >= -90 &&
      latitude <= 90 &&
      Number.isFinite(longitude) &&
      longitude >= -180 &&
      longitude <= 180
    );
  });

export const normalizeEmployeeLocations = (employee) => {
  if (Array.isArray(employee?.locations) && employee.locations.length > 0) {
    return employee.locations.map((location) => ({
      latitude: location.latitude === "" ? null : location.latitude ?? null,
      longitude: location.longitude === "" ? null : location.longitude ?? null,
    }));
  }

  if (employee?.latitude != null || employee?.longitude != null) {
    return [{ latitude: employee.latitude ?? null, longitude: employee.longitude ?? null }];
  }

  return [{ latitude: null, longitude: null }];
};

const EmployeeLocations = ({ value = [], onChange }) => {
  const locations = value.length > 0 ? value : [{ latitude: null, longitude: null }];

  const updateLocation = (index, field, coordinate) => {
    const nextLocations = locations.map((location, locationIndex) =>
      locationIndex === index ? { ...location, [field]: coordinate } : location
    );
    onChange?.(nextLocations);
  };

  const addLocation = () => {
    if (locations.length < MAX_EMPLOYEE_LOCATIONS) {
      onChange?.([...locations, { latitude: null, longitude: null }]);
    }
  };

  const removeLocation = (index) => {
    if (locations.length > 1) {
      onChange?.(locations.filter((_, locationIndex) => locationIndex !== index));
    }
  };

  return (
    <Card
      size="small"
      style={{ width: "100%", borderColor: "#d9e3ea", background: "#f8fafc" }}
      styles={{ body: { padding: 16 } }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 12,
          marginBottom: 14,
        }}
      >
        <Space align="start" size={10}>
          <EnvironmentOutlined style={{ color: "#00a8cc", fontSize: 18, marginTop: 3 }} />
          <div>
            <Typography.Text strong>GPS coordinates</Typography.Text>
            <br />
            <Typography.Text type="secondary">
              Add one or more locations for this employee.
            </Typography.Text>
          </div>
        </Space>
        <Tag color={locations.length >= MAX_EMPLOYEE_LOCATIONS ? "default" : "cyan"}>
          {locations.length} of {MAX_EMPLOYEE_LOCATIONS}
        </Tag>
      </div>

      <Space direction="vertical" size={10} style={{ width: "100%" }}>
        {locations.map((location, index) => (
          <Card
            key={index}
            size="small"
            style={{ width: "100%", borderColor: "#e5eaf0" }}
            styles={{ body: { padding: "12px 14px" } }}
          >
            <Row gutter={[12, 8]} align="bottom">
              <Col xs={24} sm={5}>
                <Tag color="blue" style={{ marginBottom: 4 }}>
                  Location {index + 1}
                </Tag>
              </Col>
              <Col xs={24} sm={8}>
                <Typography.Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                  Latitude
                </Typography.Text>
                <InputNumber
                  aria-label={`Location ${index + 1} latitude`}
                  min={-90}
                  max={90}
                  precision={8}
                  value={location.latitude}
                  onChange={(coordinate) => updateLocation(index, "latitude", coordinate)}
                  placeholder="Between -90 and 90"
                  style={{ width: "100%" }}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Typography.Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                  Longitude
                </Typography.Text>
                <InputNumber
                  aria-label={`Location ${index + 1} longitude`}
                  min={-180}
                  max={180}
                  precision={8}
                  value={location.longitude}
                  onChange={(coordinate) => updateLocation(index, "longitude", coordinate)}
                  placeholder="Between -180 and 180"
                  style={{ width: "100%" }}
                />
              </Col>
              <Col xs={24} sm={3} style={{ textAlign: "right" }}>
                <Tooltip title={locations.length === 1 ? "At least one location is required" : "Remove location"}>
                  <Button
                    aria-label={`Remove location ${index + 1}`}
                    danger
                    icon={<DeleteOutlined />}
                    disabled={locations.length === 1}
                    onClick={() => removeLocation(index)}
                  />
                </Tooltip>
              </Col>
            </Row>
          </Card>
        ))}
      </Space>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 10,
          marginTop: 14,
        }}
      >
        <Typography.Text type="secondary">
          Latitude: -90 to 90 · Longitude: -180 to 180
        </Typography.Text>
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={addLocation}
          disabled={locations.length >= MAX_EMPLOYEE_LOCATIONS}
        >
          Add location
        </Button>
      </div>
    </Card>
  );
};

export default EmployeeLocations;
